import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { BxrLogoIcon } from '@/components/ui/bxr-logo-icon'
import {
  Users,
  LayoutDashboard,
  Settings,
  Building2,
  ChevronsUpDown,
  Check,
  Shield,
  LogOut,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  UserCog,
  Medal,
  CreditCard,
  ScanLine,
  Calendar,
  PanelLeftClose,
  PanelLeft,
  Menu,
  X,
  Crown,
  Wallet,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { usePermissions } from '@/hooks/use-permissions'
import { useTenant } from '@/hooks/use-tenant'
import { useAuth } from '@/hooks/use-auth'
import { useSidebar } from '@/hooks/use-sidebar'
import { useBillingStatus } from '@/hooks/use-billing-status'
import { useTourStore } from '@/stores/use-tour-store'
import type { TourStep } from '@/stores/use-tour-store'
import type { PermissionKey } from '@/types/permission'

const SIDEBAR_TOUR_IDS: Record<string, string> = {
  '/alunos': 'sidebar-alunos',
  '/planos': 'sidebar-planos',
  '/calendario': 'sidebar-calendario',
  '/configuracoes': 'sidebar-configuracoes',
  '/usuarios': 'sidebar-usuarios',
}

interface MenuItemChild {
  label: string
  href: string
}

interface MenuItem {
  icon: LucideIcon
  label: string
  href: string
  permission: PermissionKey
  children?: MenuItemChild[]
}

const menuItems: MenuItem[] = [
  { icon: LayoutDashboard, label: 'Painel', href: '/', permission: 'dashboard:read' },
  { icon: Users, label: 'Alunos', href: '/alunos', permission: 'students:read' },
  { icon: Medal, label: 'Modalidades', href: '/modalidades', permission: 'modalities:read' },
  { icon: Calendar, label: 'Calendário', href: '/calendario', permission: 'calendar:read' },
  { icon: GraduationCap, label: 'Graduações', href: '/graduacoes', permission: 'graduation:read' },
  { icon: CreditCard, label: 'Planos', href: '/planos', permission: 'plans:read' },
  {
    icon: Wallet,
    label: 'Financeiro',
    href: '/financeiro',
    permission: 'financial:read',
    children: [
      { label: 'Contas a Receber', href: '/financeiro/contas-a-receber' },
      { label: 'Inadimplência', href: '/financeiro/inadimplencia' },
      { label: 'Resumo', href: '/financeiro/resumo' },
      { label: 'Relatórios', href: '/financeiro/relatorios' },
    ],
  },
  { icon: ScanLine, label: 'Check-in', href: '/checkin', permission: 'checkin:read' },
]

const subscriptionItem: MenuItem = {
  icon: Crown,
  label: 'Assinatura',
  href: '/assinatura',
  permission: 'subscription:read',
}

const systemItems: MenuItem[] = [
  { icon: UserCog, label: 'Usuários', href: '/usuarios', permission: 'users:read' },
  {
    icon: ScanLine,
    label: 'Configurar Check-in',
    href: '/configuracoes/checkin',
    permission: 'checkin:read',
  },
  {
    icon: Settings,
    label: 'Configurações',
    href: '/configuracoes',
    permission: 'configuracoes:read',
  },
]

function SidebarMenuItem({
  item,
  isActive,
  isCollapsed,
  onClick,
  pathname,
  tourHighlighted,
}: {
  item: MenuItem
  isActive: boolean
  isCollapsed: boolean
  onClick?: () => void
  pathname?: string
  tourHighlighted?: boolean
}) {
  const hasChildren = item.children && item.children.length > 0
  const isParentActive = hasChildren && pathname?.startsWith(item.href)
  const [isOpen, setIsOpen] = useState(isParentActive ?? false)

  if (hasChildren && !isCollapsed) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
            isParentActive
              ? 'bg-sidebar-accent text-sidebar-primary'
              : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
          )}
        >
          <item.icon className='h-5 w-5 shrink-0' />
          <span className='flex-1 truncate text-left'>{item.label}</span>
          {isOpen || isParentActive ? (
            <ChevronDown className='h-4 w-4 shrink-0' />
          ) : (
            <ChevronRight className='h-4 w-4 shrink-0' />
          )}
        </button>
        {(isOpen || isParentActive) && (
          <div className='ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-3'>
            {item.children!.map((child) => (
              <NavLink
                key={child.href}
                to={child.href}
                onClick={onClick}
                className={cn(
                  'flex items-center rounded-lg px-3 py-2 text-sm transition-colors',
                  pathname === child.href
                    ? 'font-medium text-sidebar-primary'
                    : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
                )}
              >
                <span className='truncate'>{child.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (hasChildren && isCollapsed) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'flex w-full items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium transition-colors',
              isParentActive
                ? 'bg-sidebar-accent text-sidebar-primary'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
            )}
          >
            <item.icon className='h-5 w-5 shrink-0' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side='right' sideOffset={8} align='start'>
          <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.children!.map((child) => (
            <DropdownMenuItem key={child.href} asChild>
              <NavLink
                to={child.href}
                onClick={onClick}
                className={cn(
                  'cursor-pointer',
                  pathname === child.href && 'font-medium text-sidebar-primary',
                )}
              >
                {child.label}
              </NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const content = (
    <NavLink
      to={item.href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
        isCollapsed && 'justify-center px-2',
        isActive
          ? 'bg-sidebar-accent text-sidebar-primary'
          : 'text-sidebar-foreground hover:bg-sidebar-accent/50',
        tourHighlighted && 'tour-highlight',
      )}
    >
      <item.icon className='h-5 w-5 shrink-0' />
      {!isCollapsed && <span className='truncate'>{item.label}</span>}
    </NavLink>
  )

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side='right' sideOffset={8}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return content
}

function isTourItemEnabled(href: string, isTourActive: boolean, tourStep: TourStep): boolean {
  if (!isTourActive || tourStep === 'completed') return true

  if (tourStep === 'create-admin') return href === '/usuarios'
  if (tourStep === 'ask-financial') return false
  if (tourStep === 'show-financial') return href === '/configuracoes'
  if (tourStep === 'show-students') return href === '/alunos'
  if (tourStep === 'show-plans') return href === '/planos'
  if (tourStep === 'show-calendar') return href === '/calendario'

  return false
}

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const { pathname } = useLocation()
  const { userName, userEmail, userRole, userInitials, userRoleLabel, logout } = useAuth()
  const { hasPermission } = usePermissions()
  const { currentTenant, availableTenants, switchTenant, canSwitchTenant } = useTenant()
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar()
  const { isConfigured: isBillingConfigured } = useBillingStatus()
  const { isActive: isTourActive, currentStep: tourStep, highlightTargets } = useTourStore()

  const handleLogout = () => {
    logout()
  }

  const visibleMenuItems = menuItems.filter((item) => {
    if (!hasPermission(item.permission)) return false
    if (item.href === '/financeiro' && !isBillingConfigured) return false

    return true
  })
  const visibleSystemItems = systemItems.filter((item) => hasPermission(item.permission))
  const canSeeSubscription = hasPermission(subscriptionItem.permission)
  const isFreePlan = currentTenant?.plan?.toLowerCase() === 'free'

  const collapsed = isMobile ? false : isCollapsed

  return (
    <TooltipProvider>
      <div className='flex h-full flex-col'>
        <div className='border-b border-sidebar-border px-4 py-4'>
          <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
            <BxrLogoIcon size='sm' />
            {!collapsed && (
              <span className='text-lg font-bold text-sidebar-foreground'>BxR CRM</span>
            )}
          </div>

          {canSwitchTenant && currentTenant ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {collapsed ? (
                  <button className='mt-3 flex w-full items-center justify-center rounded-md bg-sidebar-accent/50 p-2 transition-colors hover:bg-sidebar-accent'>
                    <Building2 className='h-4 w-4 text-muted-foreground' />
                  </button>
                ) : (
                  <button className='group mt-3 flex w-full items-center gap-2 rounded-md bg-sidebar-accent/50 px-3 py-2 transition-colors hover:bg-sidebar-accent'>
                    <Building2 className='h-4 w-4 shrink-0 text-muted-foreground' />
                    <div className='min-w-0 flex-1 text-left'>
                      <p className='truncate text-sm font-medium text-sidebar-foreground'>
                        {currentTenant.name}
                      </p>
                    </div>
                    <span className='shrink-0 text-xs font-medium text-primary'>
                      {currentTenant.plan}
                    </span>
                    <ChevronsUpDown className='h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-sidebar-foreground' />
                  </button>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className='w-56'
                align='start'
                side={collapsed ? 'right' : 'bottom'}
                sideOffset={8}
              >
                <DropdownMenuLabel className='flex items-center gap-2'>
                  <Shield className='h-4 w-4 text-primary' />
                  Trocar Academia
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {availableTenants.map((tenant) => (
                  <DropdownMenuItem
                    key={tenant.id}
                    onClick={() => switchTenant(tenant.id)}
                    className='flex cursor-pointer items-center justify-between'
                  >
                    <div className='flex items-center gap-2'>
                      <Building2 className='h-4 w-4 text-muted-foreground' />
                      <span className='truncate'>{tenant.name}</span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='text-xs text-muted-foreground'>{tenant.plan}</span>
                      {currentTenant.id === tenant.id && <Check className='h-4 w-4 text-primary' />}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : currentTenant ? (
            collapsed ? (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className='mt-3 flex items-center justify-center rounded-md bg-sidebar-accent/50 p-2'>
                    <Building2 className='h-4 w-4 text-muted-foreground' />
                  </div>
                </TooltipTrigger>
                <TooltipContent side='right' sideOffset={8}>
                  <div className='text-sm'>
                    <p className='font-medium'>{currentTenant.name}</p>
                    <p className='text-xs text-muted-foreground'>{currentTenant.plan}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : (
              <div className='mt-3 flex items-center gap-2 rounded-md bg-sidebar-accent/50 px-3 py-2'>
                <Building2 className='h-4 w-4 shrink-0 text-muted-foreground' />
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-medium text-sidebar-foreground'>
                    {currentTenant.name}
                  </p>
                </div>
                <span className='shrink-0 text-xs font-medium text-primary'>
                  {currentTenant.plan}
                </span>
              </div>
            )
          ) : null}
        </div>

        <nav className='flex-1 space-y-1 overflow-y-auto p-2'>
          {!collapsed && (
            <p className='mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
              Menu
            </p>
          )}
          {visibleMenuItems.map((item) => {
            const enabled = isTourItemEnabled(item.href, isTourActive, tourStep)
            const tourId = SIDEBAR_TOUR_IDS[item.href]
            const isTourHighlighted = isTourActive && !!tourId && highlightTargets.includes(tourId)

            return (
              <div
                key={item.href}
                className={cn(!enabled && 'pointer-events-none opacity-40')}
              >
                <SidebarMenuItem
                  item={item}
                  isActive={
                    item.children ? pathname.startsWith(item.href) : pathname === item.href
                  }
                  isCollapsed={collapsed}
                  onClick={onNavClick}
                  pathname={pathname}
                  tourHighlighted={isTourHighlighted}
                />
              </div>
            )
          })}

          {canSeeSubscription && isFreePlan && (
            <>
              {collapsed ? (
                <div className='my-4 border-t border-sidebar-border' />
              ) : (
                <div className='mt-6 mb-2' />
              )}
              {collapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={subscriptionItem.href}
                      onClick={onNavClick}
                      className={cn(
                        'flex items-center justify-center rounded-lg border border-amber-500/20 bg-amber-500/5 px-2 py-2.5 text-sm font-medium transition-colors hover:bg-amber-500/10',
                        pathname === subscriptionItem.href
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'text-amber-600 dark:text-amber-400',
                      )}
                    >
                      <Crown className='h-5 w-5 shrink-0' />
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side='right' sideOffset={8}>
                    Fazer upgrade
                  </TooltipContent>
                </Tooltip>
              ) : (
                <NavLink
                  to={subscriptionItem.href}
                  onClick={onNavClick}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-sm font-medium transition-colors hover:bg-amber-500/10',
                    pathname === subscriptionItem.href
                      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      : 'text-amber-600 dark:text-amber-400',
                  )}
                >
                  <Crown className='h-5 w-5 shrink-0' />
                  <div className='flex flex-1 items-center justify-between'>
                    <span className='truncate'>Fazer upgrade</span>
                    <span className='rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide'>
                      Free
                    </span>
                  </div>
                </NavLink>
              )}
            </>
          )}

          {(visibleSystemItems.length > 0 || (canSeeSubscription && !isFreePlan)) && (
            <>
              {!collapsed && (
                <p className='mb-2 mt-6 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
                  Sistema
                </p>
              )}
              {collapsed && <div className='my-4 border-t border-sidebar-border' />}
              {canSeeSubscription && !isFreePlan && (
                <SidebarMenuItem
                  item={subscriptionItem}
                  isActive={pathname === subscriptionItem.href}
                  isCollapsed={collapsed}
                  onClick={onNavClick}
                  pathname={pathname}
                />
              )}
              {visibleSystemItems.map((item) => {
                const enabled = isTourItemEnabled(item.href, isTourActive, tourStep)
                const tourId = SIDEBAR_TOUR_IDS[item.href]
                const isTourHighlighted = isTourActive && !!tourId && highlightTargets.includes(tourId)

                return (
                  <div
                    key={item.href}
                    className={cn(!enabled && 'pointer-events-none opacity-40')}
                  >
                    <SidebarMenuItem
                      item={item}
                      isActive={pathname === item.href}
                      isCollapsed={collapsed}
                      onClick={onNavClick}
                      pathname={pathname}
                      tourHighlighted={isTourHighlighted}
                    />
                  </div>
                )
              })}
            </>
          )}
        </nav>

        {!isMobile && (
          <div className='border-t border-sidebar-border p-2'>
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleSidebar}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                    collapsed && 'justify-center px-2',
                  )}
                >
                  {collapsed ? (
                    <PanelLeft className='h-5 w-5' />
                  ) : (
                    <>
                      <PanelLeftClose className='h-5 w-5' />
                      <span>Recolher menu</span>
                    </>
                  )}
                </button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side='right' sideOffset={8}>
                  Expandir menu (Ctrl+B)
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        )}

        <div className='border-t border-sidebar-border p-2'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {collapsed ? (
                <button className='flex w-full items-center justify-center rounded-lg bg-sidebar-accent/50 p-2 transition-colors hover:bg-sidebar-accent'>
                  <div className='flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground'>
                    {userInitials}
                  </div>
                </button>
              ) : (
                <button className='group flex w-full items-center gap-3 rounded-lg bg-sidebar-accent/50 p-3 transition-colors hover:bg-sidebar-accent'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground'>
                    {userInitials}
                  </div>
                  <div className='min-w-0 flex-1 text-left'>
                    <p className='truncate text-sm font-medium text-sidebar-foreground'>
                      {userName}
                    </p>
                    <p className='flex items-center gap-1 text-xs text-muted-foreground'>
                      {userRole === 'admin' && <Shield className='h-3 w-3 text-primary' />}
                      {userRoleLabel}
                    </p>
                  </div>
                  <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-sidebar-foreground' />
                </button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-56'
              align='start'
              side={collapsed ? 'right' : 'top'}
              sideOffset={8}
            >
              <DropdownMenuLabel className='font-normal'>
                <div className='flex flex-col space-y-1'>
                  <p className='text-sm font-medium'>{userName}</p>
                  <p className='text-xs text-muted-foreground'>{userEmail}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <NavLink to='/configuracoes' className='cursor-pointer'>
                  <Settings className='mr-2 h-4 w-4' />
                  Configurações
                </NavLink>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className='cursor-pointer text-red-500 focus:bg-red-500/10 focus:text-red-500'
                onClick={handleLogout}
              >
                <LogOut className='mr-2 h-4 w-4' />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </TooltipProvider>
  )
}

function DesktopSidebar() {
  const { isCollapsed } = useSidebar()

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r border-sidebar-border bg-sidebar transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
        isCollapsed ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent />
    </aside>
  )
}

function MobileSidebar() {
  const { isMobileOpen, closeMobile } = useSidebar()

  return (
    <Sheet open={isMobileOpen} onOpenChange={(open) => !open && closeMobile()}>
      <SheetContent side='left' className='w-64 bg-sidebar p-0'>
        <SheetHeader className='sr-only'>
          <SheetTitle>Menu de navegação</SheetTitle>
        </SheetHeader>
        <SidebarContent onNavClick={closeMobile} />
      </SheetContent>
    </Sheet>
  )
}

export function MobileMenuButton() {
  const { isMobile, isMobileOpen, toggleSidebar } = useSidebar()

  if (!isMobile) return null

  return (
    <button
      onClick={toggleSidebar}
      className='flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
      aria-label={isMobileOpen ? 'Fechar menu' : 'Abrir menu'}
    >
      {isMobileOpen ? <X className='h-5 w-5' /> : <Menu className='h-5 w-5' />}
    </button>
  )
}

export function Sidebar() {
  const { isMobile } = useSidebar()

  return (
    <>
      {!isMobile && <DesktopSidebar />}
      {isMobile && <MobileSidebar />}
    </>
  )
}
